<template>
  <div class="mcp-page">
    <PageHeader
      title="Web MCP server"
      subtitle="Plug ScrapingAnt into Claude, Cursor, VS Code, and other AI tools."
    />

    <SectionCard
      title="Your API key"
      subtitle="Needed by tools that authenticate with a header. Claude Desktop, claude.ai and Claude Code can sign in with your account instead."
      class="mb-2"
    >
      <CredentialField label="API key" :value="api_token || ''" secret />
      <p class="mcp-page__hint">
        New to MCP?
        <a href="https://docs.scrapingant.com/mcp-server" target="_blank"
          >Read the MCP server docs</a
        >.
      </p>
    </SectionCard>

    <SectionCard
      title="Configuration"
      subtitle="Pick your AI tool below and follow the snippet."
      class="mt-2"
    >
      <v-tabs
        v-model="tab"
        color="primary"
        show-arrows
        class="mcp-page__tabs"
      >
        <v-tab
          v-for="item in tabItems"
          :key="item"
          class="mcp-page__tab"
        >{{ item }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="mcp-page__tab-items mt-4">
        <v-tab-item v-for="(item, index) in tabItems" :key="item">
          <CodeBlock
            :code="templates[index].get_template"
            :language="templates[index].prism_language"
            :label="`${item} · config`"
          />
          <p class="mcp-page__instructions">
            <v-icon size="16" class="mcp-page__instructions-icon"
              >mdi-information-outline</v-icon
            >
            {{ templates[index].instructions }}
          </p>
        </v-tab-item>
      </v-tabs-items>
    </SectionCard>

    <SectionCard
      title="Available tools"
      subtitle="The ScrapingAnt MCP server exposes these tools to your AI agent."
      class="mt-2"
    >
      <ul class="mcp-page__tools">
        <li v-for="tool in tools" :key="tool.name">
          <code class="mcp-page__tool-name">{{ tool.name }}</code>
          <span class="mcp-page__tool-desc">{{ tool.description }}</span>
        </li>
      </ul>
    </SectionCard>

    <div class="loading-overlay" v-if="loading">
      <v-progress-circular
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import mcpTemplates from "@/helper/mcp-template-helper";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import CredentialField from "@/components/shared/CredentialField";
import CodeBlock from "@/components/shared/CodeBlock";

export default {
  name: "MCP",
  components: { PageHeader, SectionCard, CredentialField, CodeBlock },
  data() {
    return {
      tab: null,
      tabItems: [
        "Claude Desktop / claude.ai",
        "VS Code / Copilot",
        "Cursor",
        "Claude Code",
        "Cline",
        "Windsurf",
        "Other",
      ],
      api_token: "",
      loading: false,
      tools: [
        {
          name: "get_web_page_html",
          description: "Fetch a URL and return raw HTML content.",
        },
        {
          name: "get_web_page_markdown",
          description: "Fetch a URL and return content as Markdown.",
        },
        {
          name: "get_web_page_text",
          description: "Fetch a URL and return plain text content.",
        },
      ],
    };
  },
  computed: {
    templates() {
      return [
        {
          name: mcpTemplates.CLAUDE_DESKTOP.name,
          get_template: mcpTemplates.CLAUDE_DESKTOP.get(this.api_token),
          prism_language: mcpTemplates.CLAUDE_DESKTOP.prism_language,
          instructions: mcpTemplates.CLAUDE_DESKTOP.instructions,
        },
        {
          name: mcpTemplates.VSCODE.name,
          get_template: mcpTemplates.VSCODE.get(this.api_token),
          prism_language: mcpTemplates.VSCODE.prism_language,
          instructions: mcpTemplates.VSCODE.instructions,
        },
        {
          name: mcpTemplates.CURSOR.name,
          get_template: mcpTemplates.CURSOR.get(this.api_token),
          prism_language: mcpTemplates.CURSOR.prism_language,
          instructions: mcpTemplates.CURSOR.instructions,
        },
        {
          name: mcpTemplates.CLAUDE_CODE.name,
          get_template: mcpTemplates.CLAUDE_CODE.get(this.api_token),
          prism_language: mcpTemplates.CLAUDE_CODE.prism_language,
          instructions: mcpTemplates.CLAUDE_CODE.instructions,
        },
        {
          name: mcpTemplates.CLINE.name,
          get_template: mcpTemplates.CLINE.get(this.api_token),
          prism_language: mcpTemplates.CLINE.prism_language,
          instructions: mcpTemplates.CLINE.instructions,
        },
        {
          name: mcpTemplates.WINDSURF.name,
          get_template: mcpTemplates.WINDSURF.get(this.api_token),
          prism_language: mcpTemplates.WINDSURF.prism_language,
          instructions: mcpTemplates.WINDSURF.instructions,
        },
        {
          name: mcpTemplates.OTHER.name,
          get_template: mcpTemplates.OTHER.get(this.api_token),
          prism_language: mcpTemplates.OTHER.prism_language,
          instructions: mcpTemplates.OTHER.instructions,
        },
      ];
    },
  },
  methods: {
    getToken() {
      return this.$store.dispatch("dashboard/getApiToken").then(
        (res) => {
          this.api_token = res.data.token;
        },
        (err) => {
          console.log(err);
        }
      );
    },
  },
  mounted() {
    this.loading = true;
    this.getToken().then(
      () => (this.loading = false),
      () => (this.loading = false)
    );
  },
};
</script>

<style scoped lang="scss">
.mcp-page {
  position: relative;

  &__hint {
    margin: 14px 0 0;
    font-size: 12px;
    color: rgba(15, 23, 42, 0.62);

    a {
      color: #e2782f;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__tabs {
    ::v-deep .v-tabs-bar {
      background: transparent !important;
      border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    }
  }

  &__tab {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
    font-size: 13px;
  }

  &__tab-items {
    background: transparent !important;
  }

  &__instructions {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 14px 0 0;
    font-size: 12px;
    line-height: 1.55;
    color: rgba(15, 23, 42, 0.62);
  }

  &__instructions-icon {
    color: rgba(15, 23, 42, 0.45) !important;
    margin-top: 1px;
  }

  &__tools {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;

    li {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 14px 16px;
      background: #f8f8fb;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 8px;

      @media (min-width: 600px) {
        flex-direction: row;
        align-items: center;
        gap: 16px;
      }
    }
  }

  &__tool-name {
    font-family: "SF Mono", Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    color: #0f172a;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.1);
    padding: 4px 10px;
    border-radius: 6px;
    flex-shrink: 0;
    align-self: flex-start;
  }

  &__tool-desc {
    font-size: 13px;
    color: rgba(15, 23, 42, 0.7);
    line-height: 1.5;
  }
}
</style>
